import { useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useNavigateTransition } from '../lib/navigation';
import { useFlow } from '../state/flow';
import ConfirmDialog from '../components/ConfirmDialog';
import { trackEvent } from '../lib/analytics';
import { QUIZ_QUESTIONS } from '../data/quizQuestions';
import { QUIZ_COPY } from '../lib/quizCopy';
import './QuizQuestion.scss';

/** One episode quiz: forward-only stepper through 10 single-select questions.
 * No per-question feedback; Go Back quits (with confirm). */
export default function QuizQuestion() {
  const { episode } = useParams();
  const ep = Number(episode);
  const navigate = useNavigateTransition();
  const { form, setQuizResult } = useFlow();
  const questions = QUIZ_QUESTIONS[ep];

  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  // Forward-only, so we only need a running tally, not the full answer history.
  const [correctSoFar, setCorrectSoFar] = useState(0);
  const [quitOpen, setQuitOpen] = useState(false);

  // Unknown episode or no name entered yet → back to the picker.
  if (!questions || !form.firstName) return <Navigate to="/class" replace />;

  const total = questions.length;
  const q = questions[idx];

  function next() {
    if (selected === null) return;
    const correct = correctSoFar + (selected === q.correctIndex ? 1 : 0);
    if (idx + 1 < total) {
      setCorrectSoFar(correct);
      setIdx(idx + 1);
      setSelected(null);
      return;
    }
    setQuizResult({
      episode: ep,
      correct,
      total,
      firstName: form.firstName,
      lastName: form.lastName,
    });
    trackEvent('quiz_completed', { episode: ep, correct, total });
    navigate(`/class/${ep}/grading`);
  }

  return (
    <div className="quiz-q">
      <div className="quiz-q__bar">
        <button type="button" className="quiz-q__back" onClick={() => setQuitOpen(true)}>
          <span aria-hidden="true">←</span> {QUIZ_COPY.goBack}
        </button>
        <span className="quiz-q__ep">Episode {ep}</span>
        <span className="quiz-q__count">
          {idx + 1}/{total}
        </span>
      </div>
      <div className="quiz-q__progress" aria-hidden="true">
        <span
          className="quiz-q__progress-fill"
          style={{ width: `${((idx + 1) / total) * 100}%` }}
        />
      </div>

      <div className="quiz-q__body">
        <h1 className="quiz-q__prompt">{q.prompt}</h1>

        <ul className="quiz-q__options">
          {q.options.map((opt, i) => (
            <li key={opt}>
              <button
                type="button"
                className={`quiz-q__option${selected === i ? ' is-selected' : ''}`}
                aria-pressed={selected === i}
                onClick={() => setSelected(i)}
              >
                <span className="quiz-q__check" aria-hidden="true" />
                <span className="quiz-q__option-text">{opt}</span>
              </button>
            </li>
          ))}
        </ul>

        <button type="button" className="quiz-q__next" onClick={next} disabled={selected === null}>
          {QUIZ_COPY.next}
        </button>
      </div>

      <ConfirmDialog
        open={quitOpen}
        title={QUIZ_COPY.quitTitle}
        body={QUIZ_COPY.quitBody}
        confirmLabel={QUIZ_COPY.confirmYes}
        cancelLabel={QUIZ_COPY.confirmNo}
        onConfirm={() => navigate('/class')}
        onCancel={() => setQuitOpen(false)}
      />
    </div>
  );
}
